/**
 * Attendance API (issue #14) — typed wrappers over the shared `api` client,
 * mirroring courses.ts/announcements.ts's shape.
 *
 * `recording_url` on `MyAttendanceSession` is typed OPTIONAL (`recording_url?:
 * string`), never `string | null` — the backend's recording gate makes the key
 * STRUCTURALLY ABSENT for every non-excused mark (not present-but-null), so the
 * type mirrors the server's actual key-omission contract. Always branch on
 * `"recording_url" in session` (or a truthy check, since the key is never
 * present with a falsy value), never on `!== null`.
 */
import { api } from "@/lib/api/client";
import type { Modality } from "@/lib/api/courses";
import type { MeetingProvider } from "@/lib/api/tenant";
import { commitOptions, decodeBulkResult, retryOptions } from "@/lib/api/commitSeams";
import type { DestinationState, SessionCancelResult, SessionPublicChange } from "@/lib/api/sessionPortal";

export type AttendanceStatus = "present" | "absent" | "excused" | "late";
export type SessionStatus = "scheduled" | "held" | "cancelled";

export interface AttendanceStats {
  held: number;
  present: number;
  absent: number;
  excused: number;
  late: number;
  pct: number | null;
}

/** The four meeting fields a session may override. `null` on `SessionOverrides`
 *  means INHERIT — the session does not set that field and the course default
 *  stands. On `MeetingConfigPatch`, `null` means CLEAR the override (go back to
 *  inheriting); OMITTING a key leaves that override untouched. */
export interface SessionOverrides {
  modality: Modality | null;
  meeting_provider: MeetingProvider | null;
  meeting_url: string | null;
  location: string | null;
}

/** GET /courses/{course_id}/sessions row — NEVER carries recording_url.
 *
 *  `modality`/`meeting_provider`/`meeting_url`/`location` are the RESOLVED
 *  values (the session's override, else the course default), already coalesced
 *  by the `lms_session_meeting` view — never re-derive them on the client.
 *  `overrides` carries the RAW session values beside them, which is the only
 *  way to render "inherited from the course" differently from "set on this
 *  session": a resolved value alone cannot tell you which it is. */
export interface Session {
  id: string;
  course_id: string;
  session_date: string;
  starts_at: string | null;
  ends_at?: string | null;
  session_no: number | null;
  label: string | null;
  status: SessionStatus;
  created_at: string;
  updated_at: string;
  modality: Modality;
  meeting_provider: MeetingProvider;
  meeting_url: string | null;
  location: string | null;
  overrides: SessionOverrides;
  /** The roll's state, decided by the server's clock: `recorded` once any mark
   *  exists, `not_recorded` when none does and the class has ended (never an
   *  absence), `null` when not yet due, cancelled, or the viewer keeps no roll.
   *  Only the list read carries it; a meeting save's row does not. */
  roll?: "recorded" | "not_recorded" | null;
  /** Display phase from the database clock; unknown on a meeting-write response. */
  clock_phase?: "upcoming" | "scheduled_now" | "status_pending" | null;
  /** Canonical server destination; configuration above remains unchanged. */
  destination_state?: DestinationState;
  /** A safe URL only when destination_state is ready, otherwise null. */
  destination_url?: string | null;
  /** Only the public change words; actor, notice and email facts stay private. */
  latest_change?: SessionPublicChange | null;
}

/** PATCH /sessions/{session_id} body. Every key is optional; send `null` to
 *  clear an override. Sending `{}` is a 422 — it would be a write that means
 *  nothing. */
export type MeetingConfigPatch = Partial<SessionOverrides>;

/** A meeting save: the overrides to change, and the session's `updated_at` as
 *  it was read. A session saved since answers 409 with the newer row as
 *  `current`; without it the server applies no such check. */
export type MeetingConfigSave = MeetingConfigPatch & { expected_updated_at?: string };

export interface GeneratedSession {
  id: string;
  session_date: string;
  session_no: number | null;
  starts_at: string;
}

export interface RosterMark {
  status: AttendanceStatus;
  camera_on: boolean | null;
}

/** GET /courses/{course_id}/attendance — the instructor/admin register matrix. */
export interface CourseAttendance {
  sessions: {
    id: string;
    session_no: number | null;
    session_date: string;
    starts_at: string | null;
    label: string | null;
    status: SessionStatus;
  }[];
  roster: {
    student_id: string;
    legal_name: string;
    preferred_name: string | null;
    participation_kind: "credit" | "audit";
    /** The login the course inbox addresses this learner by; null without an active one. */
    recipient_user_id: string | null;
    marks: Record<string, RosterMark>;
    stats: AttendanceStats;
  }[];
}

/** GET/POST /sessions/{session_id}/attendance — one session's roster + marks. */
export interface SessionAttendance {
  session_id: string;
  course_id: string;
  session_date: string;
  starts_at: string | null;
  session_no: number | null;
  label: string | null;
  status: SessionStatus;
  roster: {
    student_id: string;
    legal_name: string;
    preferred_name: string | null;
    participation_kind: "credit" | "audit";
    status: AttendanceStatus | null;
    camera_on: boolean | null;
  }[];
  marked_count?: number;
  /** The roll's version: its latest mark's `updated_at`, or null before any
   *  learner is marked. A submit sends it back as `expected_roll_updated_at`. */
  roll_updated_at: string | null;
}

export interface AttendanceMarkInput {
  student_id: string;
  status: AttendanceStatus;
  camera_on?: boolean | null;
}

export interface MarkAttendanceBody {
  mark_all_present?: boolean;
  marks?: AttendanceMarkInput[];
  /** The roll's version these marks were made against, null for an empty
   *  roll. A roll saved since answers 409 with the newer roll as `current`. */
  expected_roll_updated_at: string | null;
}

/** GET /attendance/my — a student's own session log, grouped by course.
 * `recording_url` is present ONLY for an excused mark with a real recording. */
export interface MyAttendanceSession {
  session_id: string;
  session_no: number | null;
  session_date: string;
  starts_at: string | null;
  label: string | null;
  session_status: SessionStatus;
  /** A held session can predate this learner's first recorded mark. */
  status: AttendanceStatus | null;
  camera_on: boolean | null;
  recording_url?: string;
}

export interface MyAttendanceCourse {
  course_id: string;
  code: string;
  title: string;
  sessions: MyAttendanceSession[];
  stats: AttendanceStats;
}

export interface MyAttendance {
  courses: MyAttendanceCourse[];
}

/** A plan's state as All sessions says it; a learner only ever reads "published". */
export type RegisterPlanState = "draft" | "changed" | "published";

/** One row of All sessions: the session list's row and its plan, if the reader may see one. */
export interface RegisterSession extends Session {
  plan: { id: string; state: RegisterPlanState; updated_at: string } | null;
}

/** C07/C10: the course's sessions with their plans, and the plans not yet on a dated session, in one read (R1). */
export interface SessionRegister {
  /** Only a published course tells its students about a session change (CC2). */
  course_status?: "draft" | "published" | "archived" | null;
  sessions: RegisterSession[];
  /** Plans on no session: the teaching team's saved drafts, a learner's publications. Recap and next steps wait for a held session. */
  undated_plans: {
    id: string; position: number; topic: string; state: RegisterPlanState; updated_at: string;
    learning_objectives: string[]; preparation_guidance: string | null;
  }[];
}

export const attendanceApi = {
  getPeopleRanks: (courseId: string) => api.get<{ state: string; value?: { candidates: { id: string; rank: number | null; eligible: boolean; blocked: boolean; read: string }[] } }>(`/courses/${encodeURIComponent(courseId)}/people-ranks`),
  sessionRegister: (courseId: string) => api.get<SessionRegister>(`/courses/${courseId}/sessions/register`),
  /**
   * Cancel the chosen sessions under one commit key. A retry sends the same
   * key, versions and note with only the failed ids (CASE-029).
   */
  cancelSessions: async (courseId: string, body: { session_ids: string[]; expected_updated_at: Record<string, string>; note: string; tell_students: boolean }, key: string, retry = false): Promise<SessionCancelResult> => {
    const result = await api.post<SessionCancelResult>(`/courses/${courseId}/sessions/cancel`, body, retry ? retryOptions(key) : commitOptions(key));
    return { ...result, ...decodeBulkResult(result) };
  },
  generateSessions: (courseId: string) =>
    api.post<GeneratedSession[]>(`/courses/${courseId}/sessions/generate`),
  listSessions: (courseId: string) => api.get<Session[]>(`/courses/${courseId}/sessions`),
  updateSessionMeeting: (sessionId: string, body: MeetingConfigSave) =>
    api.patch<Session>(`/sessions/${sessionId}`, body),
  getCourseAttendance: (courseId: string) =>
    api.get<CourseAttendance>(`/courses/${courseId}/attendance`),
  getSessionAttendance: (sessionId: string) =>
    api.get<SessionAttendance>(`/sessions/${sessionId}/attendance`),
  markAttendance: (sessionId: string, body: MarkAttendanceBody, key: string = crypto.randomUUID()) =>
    api.post<SessionAttendance>(`/sessions/${sessionId}/attendance`, body, commitOptions(key)),
  getMyAttendance: () => api.get<MyAttendance>("/attendance/my"),
};
